import { createTRPCReact } from "@trpc/react-query";
import { createTRPCClient, httpLink } from "@trpc/client";
import {
  BACKEND_LANG_HEADER,
  getCurrentLocale,
  mapLocaleToBackendCode,
} from "@bitsentry-ce/i18n";
import type { AppRouter } from "../../../.generated-trpc-types/apps/backend/src/app.router";
import {
  clearTokens,
  getRefreshToken,
  getToken,
  replaceRefreshToken,
  replaceToken,
} from "../auth/utils";
import { analyticsLink } from "./analytics-link";

// Create the tRPC React hooks
export const trpc = createTRPCReact<AppRouter>();

function getViteString(value: unknown, fallback: string): string {
  if (typeof value === "string" && value !== "") {
    return value;
  }

  return fallback;
}

function getApiBaseUrl(): string {
  const configuredUrl = getViteString(import.meta.env.VITE_API_URL, "");
  if (configuredUrl !== "") {
    return configuredUrl;
  }

  if (import.meta.env.DEV) {
    return "http://localhost:3000";
  }

  throw new Error("VITE_API_URL must be configured for production builds.");
}

const apiUrl = `${getApiBaseUrl()}/trpc`;

let refreshPromise: Promise<string | null> | null = null;

function buildLangHeader(): Record<string, string> {
  return { [BACKEND_LANG_HEADER]: mapLocaleToBackendCode(getCurrentLocale()) };
}

function buildAuthorizationHeaders(
  token: string | null | undefined,
): Record<string, string> {
  const langHeader = buildLangHeader();
  if (token === null || token === undefined || token === "") {
    return langHeader;
  }

  return { authorization: `Bearer ${token}`, ...langHeader };
}

const refreshClient = createTRPCClient<AppRouter>({
  links: [
    httpLink({
      url: apiUrl,
      headers: () => {
        const refreshToken = getRefreshToken();
        return buildAuthorizationHeaders(refreshToken);
      },
    }),
  ],
});

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null || refreshToken === "") {
    clearTokens();
    return null;
  }

  if (refreshPromise === null) {
    const nextRefreshPromise: Promise<{
      refreshToken: string;
      token: string;
    }> = refreshClient.authRouter.refreshWorkos.mutate({
      refreshToken,
    });

    refreshPromise = nextRefreshPromise
      .then((response) => {
        replaceToken(response.token);
        replaceRefreshToken(response.refreshToken);
        return response.token;
      })
      .catch(() => {
        clearTokens();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

function getFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof Request) {
    return input.url;
  }

  return input.toString();
}

function createAuthAwareFetch(): typeof fetch {
  return async (input, init) => {
    const response = await fetch(input, init);
    if (response.status !== 401) {
      return response;
    }

    const url = getFetchUrl(input);
    if (url.includes("authRouter.refresh")) {
      clearTokens();
      return response;
    }

    const nextToken = await refreshAccessToken();
    if (nextToken === null || nextToken === "") {
      return response;
    }

    const headers = new Headers(init?.headers);
    headers.set("authorization", `Bearer ${nextToken}`);

    return fetch(input, {
      ...init,
      headers,
    });
  };
}

export function createTrpcLinks() {
  return [
    analyticsLink<AppRouter>(),
    httpLink({
      url: apiUrl,
      fetch: createAuthAwareFetch(),
      headers: () => {
        return buildAuthorizationHeaders(getToken());
      },
    }),
  ];
}

// Create the tRPC client for direct usage if needed
// Note: Using httpLink instead of httpBatchLink to avoid batching issues with Railway's proxy
export const trpcClient = createTRPCClient<AppRouter>({
  links: createTrpcLinks(),
});
